import { Link } from 'react-router-dom';
import { cn, SegmentedControl } from '@eo/shared';

import {
  ROLE_BAR_CLASS,
  ROLE_DOT_CLASS,
  ROLE_LABELS,
  TEAM_LOAD_SCOPE_LABELS,
  TEAM_LOAD_SCOPES,
} from './constants';
import { formatHours, monthShare, teamLoadRows } from './helpers';
import { LoadBar } from './LoadBar';
import type { TeamLoadProps } from './types';

// One shared scale, so a longer bar is always more hours, person to person.
export function TeamLoad({
  users,
  across,
  progress,
  personHref,
  scope,
  onScopeChange,
}: TeamLoadProps) {
  const rows = teamLoadRows(users, across, scope ?? 'portfolio');
  const where = scope === 'all' ? 'across every portfolio' : 'on this portfolio';

  const toggle = scope && (
    <SegmentedControl
      options={TEAM_LOAD_SCOPES.map((value) => ({ value, label: TEAM_LOAD_SCOPE_LABELS[value] }))}
      value={scope}
      onChange={onScopeChange}
      ariaLabel="Team load"
      className="w-fit"
    />
  );

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {toggle}
        <p className="text-sm text-text-tertiary">
          Nobody is assigned to or has logged {where} this month.
        </p>
      </div>
    );
  }

  const scale = Math.max(...rows.map((r) => Math.max(r.assigned, r.actual)));
  const share = monthShare(progress);

  return (
    <div className="flex flex-col gap-4">
      {toggle}
      <ul
        data-testid="team-load-legend"
        className="flex flex-wrap gap-4 text-xs text-text-tertiary"
      >
        <li className="flex items-center gap-2">
          <span className="size-2.5 rounded-[2px] bg-capacity-cfo" />
          Logged
        </li>
        <li className="flex items-center gap-2">
          <span className="h-3 w-[2px] bg-text-primary" />
          Assigned
        </li>
        <li className="flex items-center gap-2">
          <span className="h-3 border-l-2 border-dotted border-text-tertiary" />
          Where logging should be today
        </li>
      </ul>
      <ul className="flex flex-col gap-5">
        {rows.map((row) => {
          const over = row.actual - row.assigned;
          return (
            <li key={row.userId} data-testid="team-load-row" className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="flex items-center gap-2 text-text-primary">
                  <span className={cn('size-2.5 rounded-[2px]', ROLE_DOT_CLASS[row.role])} />
                  {row.onPortfolio ? (
                    <Link
                      to={personHref(row.userId)}
                      className="underline-offset-4 hover:underline focus-visible:underline"
                    >
                      {row.name}
                    </Link>
                  ) : (
                    <span>{row.name}</span>
                  )}
                  <span className="text-xs text-text-tertiary">{ROLE_LABELS[row.role]}</span>
                </span>
                <span className="tabular-nums text-text-secondary">
                  {row.assigned > 0
                    ? `${formatHours(row.actual)} of ${formatHours(row.assigned)}`
                    : `Not assigned · ${formatHours(row.actual)} logged`}
                  {row.assigned > 0 && over > 0 && (
                    <span className="ml-2 text-destructive">{formatHours(over)} over</span>
                  )}
                </span>
              </div>
              <LoadBar
                label={
                  row.assigned > 0
                    ? `${row.name}: ${formatHours(row.actual)} logged of ${formatHours(row.assigned)} assigned`
                    : `${row.name}: ${formatHours(row.actual)} logged without an assignment`
                }
                scale={scale}
                target={row.assigned}
                targetHint={`Assigned ${formatHours(row.assigned)}: the hours ${row.name} holds ${where} this month. Hours logged past this line are over.`}
                pace={row.assigned * share}
                paceHint={`Where logging should be today, ${formatHours(row.assigned * share)}: the assigned hours times the share of the month’s working days already gone.`}
                segments={[
                  {
                    key: 'logged',
                    hours: row.actual,
                    className: ROLE_BAR_CLASS[row.role].logged,
                  },
                ]}
              />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
